import React from "react";
import _ from "lodash";
import PropTypes from "prop-types";
import Chart from "../../../parts/chart";
import ToggleLinksInput from "../../../parts/inputs/toggle-links-input";
import getDayRangeString from "../../../utils/day-range-string";

import { PRIMARIES } from "../../../constants";

function View(props, { locals }) {
  _.defaults(locals.query, {
    primary: "total",
    units: "metric",
  });

  const { snapshot, query, momentTZ } = locals;
  const { units, primary } = query;
  const type = _.get(snapshot, "data.type");
  const competitionsVolume = _.get(
    snapshot,
    "data.activeCompetitionsVolume",
    {}
  );
  const filteredCompetitionsVolume = type
    ? _.filter(competitionsVolume, { type: type })
    : competitionsVolume;

  const keys = _(filteredCompetitionsVolume)
    .map("date")
    .sort()
    .map((k) => momentTZ(k).startOf("day").toJSON())
    .uniq()
    .value();
  const labels = _.map(keys, (k) => momentTZ(k).format("MMM D/YY"));

  const selectedPrimaries = primary === "total" ? PRIMARIES : [primary];
  const seriesTotals = _.map(selectedPrimaries, (name) => {
    return {
      name,
      className: name,
      data: _.map(keys, (k) => {
        const competition = _.find(
          _.sortBy(filteredCompetitionsVolume, "date"),
          ({ date }) => {
            return momentTZ(date).startOf("day").isSame(momentTZ(k));
          }
        );
        const attempts = _.get(competition, ["attempts", [name]], []);
        return locals.fromMetric(_.max(attempts.concat(0.1)), units, false);
      }),
    };
  });
  const seriesWilks = _.map(keys, (k) => {
    const competition = _.find(
      _.sortBy(filteredCompetitionsVolume, "date"),
      ({ date }) => {
        return momentTZ(date).startOf("day").isSame(momentTZ(k));
      }
    );

    return _.get(competition, "wilks", 0);
  });

  const totalVolumes = _.map(keys, (k, i) =>
    _.sum(_.map(seriesTotals, `data.${i}`))
  );
  const maxVolume = _.max(totalVolumes);
  const maxWilks = _.max(seriesWilks);

  return (
    <div className="body metrics">
      <h1 className="alt">Competition History</h1>
      <p>
        Analyze your performance from each competition, including totals and
        Wilks, and compare with past results.
      </p>

      <div className="Chart-Frame bar-chart">
        <div className="head">
          <div className="right-side">
            <div className="group">
              <ToggleLinksInput
                name="units"
                options={[
                  { text: "kg", value: "metric" },
                  { text: "lbs", value: "imperial" },
                ]}
              />
            </div>
          </div>

          <div className="left-side">
            <div className="group">
              {!_.includes(type, "Bench Only") && (
                <ToggleLinksInput
                  className="tabs"
                  name="primary"
                  options={[
                    { text: "Total", value: "total" },
                    { text: "Squat", value: "squat" },
                    { text: "Bench", value: "bench" },
                    { text: "Deadlift", value: "deadlift" },
                  ]}
                />
              )}
            </div>
          </div>
        </div>

        <div className="body">
          {!_.isEmpty(filteredCompetitionsVolume) && (
            <div>
              <div className="legend">
                <div className="line series-a">Wilks</div>
              </div>
              <div className="axis-labels">
                <div className="left">Total</div>
                <div className="right">Wilks</div>
              </div>
              <div className="chart-fullsize">
                <div className="labels">
                  <Chart
                    type="Bar"
                    only="label"
                    data={{ series: seriesTotals }}
                    options={{
                      width: "60px",
                      height: "380px",
                      stackBars: true,
                      axisX: { showGrid: false, showLabel: false },
                      axisY: {
                        showGrid: false,
                        showLabel: true,
                        scaleMinSpace: 30,
                        onlyInteger: true,
                        high: maxVolume,
                        low: 0,
                      },
                    }}
                  />

                  <Chart
                    type="Bar"
                    only="label"
                    data={{ series: [seriesWilks] }}
                    options={{
                      width: "60px",
                      height: "380px",
                      axisX: { showGrid: false, showLabel: false },
                      axisY: {
                        showGrid: false,
                        showLabel: true,
                        onlyInteger: true,
                        scaleMinSpace: 30,
                        high: maxWilks,
                        low: 0,
                      },
                    }}
                  />
                </div>

                <div className="data">
                  <Chart
                    type="Bar"
                    data={{
                      labels: labels,
                      series: seriesTotals,
                    }}
                    options={{
                      width: `${_.size(labels) * 40}px`,
                      height: "380px",
                      stackBars: true,
                      seriesBarDistance: 20,
                      chartPadding: { left: 35, right: 75 },
                      axisX: { showGrid: false, showLabel: true },
                      axisY: {
                        showGrid: true,
                        showLabel: false,
                        scaleMinSpace: 30,
                      },
                    }}
                    tooltip={(item, i) => {
                      const key = keys[i];
                      const date = momentTZ(key);
                      const dayRange = getDayRangeString([date, date]);
                      if (totalVolumes[i] <= 0.4) return;
                      return (
                        <div className="tooltip" style={{ left: item.x }}>
                          <div className="details">
                            <div>
                              <b>{dayRange}</b>
                            </div>
                            <div>
                              Wilks <b>{seriesWilks[i]}</b>
                            </div>
                            {_.compact(
                              _.map(seriesTotals, ({ name, data }) => {
                                if (!data[i] || data[i] === 0.1) return;
                                return (
                                  <div key={name}>
                                    {_.capitalize(name)}{" "}
                                    <b>
                                      {locals.withWeight(
                                        _.round(data[i], 1),
                                        units
                                      )}
                                    </b>{" "}
                                    {_.round(
                                      (data[i] / totalVolumes[i]) * 100,
                                      1
                                    )}
                                    %
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      );
                    }}
                  />

                  {_.size(labels) >= 2 && (
                    <Chart
                      type="Line"
                      data={{ series: [seriesWilks] }}
                      options={{
                        width: `${_.size(labels) * 40}px`,
                        height: "380px",
                        showPoint: false,
                        chartPadding: { left: 35, right: 75 },
                        axisX: { showGrid: false, showLabel: false },
                        axisY: {
                          scaleMinSpace: 30,
                          onlyInteger: true,
                          showGrid: false,
                          showLabel: false,
                          high: maxWilks,
                          low: 0,
                        },
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

View.contextTypes = { locals: PropTypes.object };

export default View;
